import { useEffect, useState, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { ArrowLeft, ExternalLink, Share2, Edit3, Trash2, Heart, ChevronLeft, ChevronRight, Bell, BellOff } from 'lucide-react';
import { useAuthStore, useArticlesStore, useLikedArticlesStore, useFeedsStore, useFoldersStore } from '../store/useStore';
import { articles as articlesApi, comments as commentsApi, social as socialApi } from '../lib/api';
import { trackEvent } from '../lib/analytics';
import MentionAutocomplete from './MentionAutocomplete';
import { parseAtMentions } from '../utils/mentions';
import { renderMentionsAsLinks } from '../utils/renderMentions';
import { openExternalLink } from '../utils/openExternalLink';
import { decodeHtmlEntities } from '../utils/decodeHtml';
import { formatAge, stamp } from '../utils/age';
import CommentLinkEmbed from './CommentLinkEmbed';
import toast from 'react-hot-toast';

interface Article {
  id: string;
  title: string;
  content: string | null;
  excerpt?: string | null;
  url: string;
  author?: string | null;
  imageUrl?: string | null;
  publishedAt?: string;
  isLiked: boolean;
  isRead?: boolean;
  feed: {
    id: string;
    title: string;
  };
  _count: {
    likes: number;
  };
  comments: Array<{
    id: string;
    content: string;
    links?: string | null;
    createdAt: string;
    user: {
      id: string;
      username: string;
      displayName: string | null;
      avatar: string | null;
    };
  }>;
  shares: Array<{
    id: string;
    note?: string;
    isPublic: boolean;
    createdAt: string;
    user: {
      id: string;
      username: string;
      displayName: string | null;
      avatar: string | null;
    };
  }>;
}

function readingMinutes(html: string): number {
  const text = html.replace(/<[^>]+>/g, ' ');
  const words = text.trim().split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 230));
}

export default function ArticleReader() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const token = useAuthStore((state) => state.token)!;
  const user = useAuthStore((state) => state.user)!;
  const updateArticle = useArticlesStore((state) => state.updateArticle);
  const articles = useArticlesStore((state) => state.articles);
  const sharedArticles = useArticlesStore((state) => state.sharedArticles);
  const currentContext = useArticlesStore((state) => state.currentContext);
  const filter = useArticlesStore((state) => state.filter);
  const selectedFeedId = useFeedsStore((state) => state.selectedFeedId);
  const selectedFolderId = useFeedsStore((state) => state.selectedFolderId);
  const decrementFeedUnreadCount = useFeedsStore((state) => state.decrementUnreadCount);
  const folders = useFoldersStore((state) => state.folders);
  const decrementFolderUnreadCount = useFoldersStore((state) => state.decrementUnreadCount);
  const { addLikedArticle, removeLikedArticle, isArticleLiked } = useLikedArticlesStore();
  const [article, setArticle] = useState<Article | null>(null);
  const [totalArticleCount, setTotalArticleCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [commentText, setCommentText] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareNote, setShareNote] = useState('');
  const [highlightPopover, setHighlightPopover] = useState<{ x: number; y: number; text: string } | null>(null);
  const articleContentRef = useRef<HTMLDivElement | null>(null);
  const [isPublicShare, setIsPublicShare] = useState(user?.defaultPublicShares ?? false);
  const [submittingShare, setSubmittingShare] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editingCommentText, setEditingCommentText] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [users, setUsers] = useState<any[]>([]);
  const [shareCursorPosition, setShareCursorPosition] = useState(0);
  const [commentCursorPosition, setCommentCursorPosition] = useState(0);
  const shareTextareaRef = useRef<HTMLTextAreaElement>(null);
  const commentTextareaRef = useRef<HTMLTextAreaElement>(null);
  const articleContainerRef = useRef<HTMLDivElement>(null);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [checkingSubscription, setCheckingSubscription] = useState(true);

  useEffect(() => {
    async function loadArticle() {
      if (!id) return;

      setLoading(true);

      try {
        const data = await articlesApi.getOne(token, id);
        setArticle(data);
        trackEvent('article_read');

        if (data.isLiked) {
          addLikedArticle(data.id);
        }

        window.scrollTo(0, 0);
        const scrollableContainers = document.querySelectorAll('.overflow-y-auto');
        scrollableContainers.forEach(container => {
          container.scrollTop = 0;
        });

        if (!data.isRead) {
          try {
            await articlesApi.markRead(token, data.id);
            updateArticle(data.id, { isRead: true });
            setArticle(prev => prev ? { ...prev, isRead: true } : prev);
            decrementFeedUnreadCount(data.feed.id);
            const parentFolder = folders.find(f =>
              f.subscriptions?.some(sub => sub.feed?.id === data.feed.id)
            );
            if (parentFolder) {
              decrementFolderUnreadCount(parentFolder.id);
            }
          } catch (error) {
            console.error('Failed to mark article as read:', error);
          }
        }
      } catch (error) {
        console.error('Failed to load article:', error);
        toast.error('Failed to load article');
        navigate('/read');
      } finally {
        setLoading(false);
      }
    }

    loadArticle();
  }, [id, token, navigate, addLikedArticle, updateArticle]);

  // True total for the position counter (unread view only)
  useEffect(() => {
    async function fetchTotalCount() {
      if (currentContext !== 'articles' || filter !== 'unread') {
        setTotalArticleCount(null);
        return;
      }
      try {
        const { count } = await articlesApi.getUnreadCount(
          token,
          selectedFeedId || undefined,
          selectedFolderId || undefined
        );
        setTotalArticleCount(count);
      } catch (error) {
        console.error('Failed to fetch total article count:', error);
        setTotalArticleCount(null);
      }
    }
    fetchTotalCount();
  }, [token, currentContext, filter, selectedFeedId, selectedFolderId]);

  useEffect(() => {
    if (articleContainerRef.current) {
      articleContainerRef.current.scrollTop = 0;
    }
  }, [id]);

  useEffect(() => {
    async function loadUsers() {
      try {
        const allUsers = await socialApi.getAllUsers(token);
        setUsers(allUsers);
      } catch (error) {
        console.error('Failed to load users for mentions:', error);
      }
    }

    loadUsers();
  }, [token]);

  // Highlight-to-share popover
  useEffect(() => {
    function handleSelectionChange() {
      const selection = window.getSelection();
      if (!selection || selection.isCollapsed || !articleContentRef.current) {
        setHighlightPopover(null);
        return;
      }
      const range = selection.getRangeAt(0);
      if (!articleContentRef.current.contains(range.commonAncestorContainer)) {
        setHighlightPopover(null);
        return;
      }
      const text = selection.toString().trim();
      if (text.length < 8) {
        setHighlightPopover(null);
        return;
      }
      const rect = range.getBoundingClientRect();
      setHighlightPopover({
        x: rect.left + rect.width / 2,
        y: rect.top,
        text,
      });
    }

    document.addEventListener('selectionchange', handleSelectionChange);
    return () => document.removeEventListener('selectionchange', handleSelectionChange);
  }, [id]);

  function shareWithQuote() {
    if (!highlightPopover) return;
    setShareNote(`> ${highlightPopover.text}\n\n`);
    setShowShareModal(true);
    setHighlightPopover(null);
    window.getSelection()?.removeAllRanges();
  }

  useEffect(() => {
    async function checkSubscription() {
      if (!id) return;

      setCheckingSubscription(true);
      try {
        const { subscribed } = await articlesApi.getSubscription(token, id);
        setIsSubscribed(subscribed);
      } catch (error) {
        console.error('Failed to check subscription status:', error);
      } finally {
        setCheckingSubscription(false);
      }
    }

    checkSubscription();
  }, [id, token]);

  const currentArticleList = currentContext === 'shared' ? sharedArticles : articles;
  const currentIndex = currentArticleList.findIndex(a => a.id === id);
  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex >= 0 && currentIndex < currentArticleList.length - 1;
  const displayTotalCount = totalArticleCount !== null ? totalArticleCount : currentArticleList.length;
  const previousArticle = hasPrevious ? currentArticleList[currentIndex - 1] : null;
  const nextArticle = hasNext ? currentArticleList[currentIndex + 1] : null;

  useEffect(() => {
    function handleKeyPress(e: KeyboardEvent) {
      if (document.activeElement?.tagName === 'INPUT' ||
          document.activeElement?.tagName === 'TEXTAREA') {
        return;
      }

      switch (e.key.toLowerCase()) {
        case 'j':
          e.preventDefault();
          if (nextArticle) {
            navigate(`/article/${nextArticle.id}`);
          }
          break;
        case 'k':
          e.preventDefault();
          if (previousArticle) {
            navigate(`/article/${previousArticle.id}`);
          }
          break;
        case 's':
          e.preventDefault();
          setShowShareModal(true);
          break;
        case 'escape':
          e.preventDefault();
          navigate(-1);
          break;
      }
    }

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [navigate, nextArticle, previousArticle, currentContext]);

  async function handleToggleLike() {
    if (!article) return;

    try {
      const liked = isArticleLiked(article.id);
      if (liked) {
        await articlesApi.unlike(token, article.id);
        removeLikedArticle(article.id);
      } else {
        await articlesApi.like(token, article.id);
        addLikedArticle(article.id);
        trackEvent('article_like');
      }
      setArticle({ ...article, isLiked: !liked });
      updateArticle(article.id, { isLiked: !liked });
    } catch {
      toast.error('Failed to update article');
    }
  }

  async function handleToggleSubscription() {
    if (!article) return;

    try {
      if (isSubscribed) {
        await articlesApi.unsubscribe(token, article.id);
        setIsSubscribed(false);
        toast.success('Comment notifications off');
      } else {
        await articlesApi.subscribe(token, article.id);
        setIsSubscribed(true);
        toast.success('Comment notifications on');
      }
    } catch {
      toast.error('Failed to update subscription');
    }
  }

  async function handleSubmitComment(e: React.FormEvent) {
    e.preventDefault();
    if (!article || !commentText.trim()) return;

    setSubmittingComment(true);

    try {
      const commentData: any = {
        articleId: article.id,
        content: commentText.trim(),
      };

      const mentions = parseAtMentions(commentText);
      if (mentions.length > 0) {
        commentData.mentions = mentions.map(m => m.username);
      }

      const newComment = await commentsApi.create(token, commentData);
      trackEvent('comment_create');

      setArticle({
        ...article,
        comments: [...article.comments, newComment],
      });
      setCommentText('');
      setIsSubscribed(true);
      toast.success('Comment added');
    } catch {
      toast.error('Failed to add comment');
    } finally {
      setSubmittingComment(false);
    }
  }

  async function handleShare(e: React.FormEvent) {
    e.preventDefault();
    if (!article) return;

    setSubmittingShare(true);

    try {
      const shareData: any = {
        articleId: article.id,
        note: shareNote.trim() || undefined,
        isPublic: isPublicShare,
      };

      if (shareNote.trim()) {
        const mentions = parseAtMentions(shareNote);
        if (mentions.length > 0) {
          shareData.mentions = mentions.map(m => m.username);
        }
      }

      await socialApi.share(token, shareData);
      trackEvent('article_share');

      setShowShareModal(false);
      setShareNote('');
      setIsPublicShare(user?.defaultPublicShares ?? false);
      toast.success('Shared');
    } catch (error: any) {
      if (error?.message === 'You have already shared this article') {
        toast('Already shared. Delete the earlier share to share again.', { duration: 4000 });
      } else {
        toast.error('Failed to share article');
      }
    } finally {
      setSubmittingShare(false);
    }
  }

  function handleSelectMention(username: string, startIndex: number, endIndex: number) {
    const newText = shareNote.substring(0, startIndex) + `@${username} ` + shareNote.substring(endIndex);
    setShareNote(newText);
    const newCursorPosition = startIndex + username.length + 2;
    setShareCursorPosition(newCursorPosition);
    if (shareTextareaRef.current) {
      shareTextareaRef.current.focus();
      setTimeout(() => {
        shareTextareaRef.current?.setSelectionRange(newCursorPosition, newCursorPosition);
      }, 0);
    }
  }

  function handleSelectCommentMention(username: string, startIndex: number, endIndex: number) {
    const newText = commentText.substring(0, startIndex) + `@${username} ` + commentText.substring(endIndex);
    setCommentText(newText);
    const newCursorPosition = startIndex + username.length + 2;
    setCommentCursorPosition(newCursorPosition);
    if (commentTextareaRef.current) {
      commentTextareaRef.current.focus();
      setTimeout(() => {
        commentTextareaRef.current?.setSelectionRange(newCursorPosition, newCursorPosition);
      }, 0);
    }
  }

  function handleMentionClick(username: string) {
    const target = users.find(u => u.username === username);
    if (target) {
      navigate(`/user/${target.id}`);
    }
  }

  function handleEditComment(commentId: string, currentText: string) {
    setEditingCommentId(commentId);
    setEditingCommentText(currentText);
  }

  function handleCancelEdit() {
    setEditingCommentId(null);
    setEditingCommentText('');
  }

  async function handleSaveEdit(commentId: string) {
    if (!editingCommentText.trim()) return;

    try {
      const updatedComment = await commentsApi.update(token, commentId, editingCommentText.trim());

      if (article) {
        setArticle({
          ...article,
          comments: article.comments.map(comment =>
            comment.id === commentId ? updatedComment : comment
          ),
        });
      }

      setEditingCommentId(null);
      setEditingCommentText('');
      toast.success('Comment updated');
    } catch {
      toast.error('Failed to update comment');
    }
  }

  async function handleDeleteComment(commentId: string) {
    try {
      await commentsApi.delete(token, commentId);

      if (article) {
        setArticle({
          ...article,
          comments: article.comments.filter(comment => comment.id !== commentId),
        });
      }

      setShowDeleteConfirm(null);
      toast.success('Comment deleted');
    } catch {
      toast.error('Failed to delete comment');
    }
  }

  // Comments and share notes, newest first
  const discussion = useMemo(() => {
    if (!article) return [];
    const items: Array<{
      id: string;
      type: 'comment' | 'share';
      content: string;
      links?: string | null;
      createdAt: string;
      user: { id: string; username: string; displayName: string | null; avatar: string | null };
      isPublic?: boolean;
    }> = [
      ...article.comments.map(comment => ({
        id: comment.id,
        type: 'comment' as const,
        content: comment.content,
        links: comment.links,
        createdAt: comment.createdAt,
        user: comment.user,
      })),
      ...article.shares
        .filter(share => share.note)
        .map(share => ({
          id: share.id,
          type: 'share' as const,
          content: share.note!,
          createdAt: share.createdAt,
          user: share.user,
          isPublic: share.isPublic,
        })),
    ];
    items.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    return items;
  }, [article]);

  if (loading) {
    return (
      <div className="pl-4 lg:pl-12 py-10">
        <span className="kicker">Loading…</span>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="pl-4 lg:pl-12 py-10 max-w-prose">
        <div className="kicker mb-1">Not found</div>
        <p className="text-ink-secondary mb-3">This article could not be loaded.</p>
        <button onClick={() => navigate('/read')} className="btn">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to Read
        </button>
      </div>
    );
  }

  const content = (article.content || '').trim();
  const hasContent = content.length > 0;
  const liked = isArticleLiked(article.id);
  const minutes = hasContent ? readingMinutes(content) : null;
  const showHero = !!article.imageUrl && !content.includes(article.imageUrl);
  // № 012 / 169 when the list context is known; otherwise the cell is hidden
  const position = currentIndex >= 0
    ? `${stamp(currentIndex)} / ${displayTotalCount}`
    : null;

  return (
    <div ref={articleContainerRef} className="flex-1 flex flex-col bg-canvas overflow-x-hidden" style={{ touchAction: 'pan-y' }}>
      {/* Mono toolbar with shared borders */}
      <div className="sticky top-0 z-10 flex items-stretch border-b border-edge bg-canvas overflow-x-auto">
        <button onClick={() => navigate(-1)} className="tool" title="Back (Esc)">
          <ArrowLeft className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Back</span>
        </button>
        <button onClick={handleToggleLike} className={`tool ${liked ? 'is-on' : ''}`} title="Like (l)">
          <Heart className="w-3.5 h-3.5" fill={liked ? 'currentColor' : 'none'} />
          <span className="hidden sm:inline">{liked ? 'Liked' : 'Like'}</span>
        </button>
        <button onClick={() => setShowShareModal(true)} className="tool" title="Share (s)">
          <Share2 className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Share</span>
        </button>
        <button
          onClick={handleToggleSubscription}
          disabled={checkingSubscription}
          className={`tool ${isSubscribed ? 'is-on' : ''}`}
          title={isSubscribed ? 'Stop comment notifications' : 'Get comment notifications'}
        >
          {isSubscribed ? <Bell className="w-3.5 h-3.5" fill="currentColor" /> : <BellOff className="w-3.5 h-3.5" />}
          <span className="hidden sm:inline">Follow</span>
        </button>
        <a
          href={article.url}
          onClick={(e) => { e.preventDefault(); openExternalLink(article.url); }}
          className="tool"
          title="Open the source"
        >
          <span className="hidden sm:inline">Source</span> <ExternalLink className="w-3.5 h-3.5" />
        </a>
        <span className="flex-1" />
        <button
          onClick={() => previousArticle && navigate(`/article/${previousArticle.id}`)}
          disabled={!hasPrevious}
          className="tool border-l border-edge"
          title={hasPrevious ? `Previous: ${previousArticle?.title}` : 'No previous article'}
        >
          <ChevronLeft className="w-3.5 h-3.5" />
        </button>
        {position && <span className="tool text-ink-muted">{position}</span>}
        <button
          onClick={() => nextArticle && navigate(`/article/${nextArticle.id}`)}
          disabled={!hasNext}
          className="tool border-r-0"
          title={hasNext ? `Next: ${nextArticle?.title}` : 'No next article'}
        >
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Left-rail prose at the reading measure */}
      <article className="pl-4 pr-4 lg:pl-12 lg:pr-8 py-8">
        <div className="measure-prose">
          <div className="kicker mb-3">
            {article.feed.title}
            {article.author && <> · {article.author}</>}
            {article.publishedAt && <> · {format(new Date(article.publishedAt), 'MMM d, yyyy')}</>}
            {minutes !== null && <> · {minutes} min</>}
          </div>
          <h1 className="text-[1.75rem] sm:text-[2.125rem] mb-6">
            <a
              href={article.url}
              onClick={(e) => { e.preventDefault(); openExternalLink(article.url); }}
              className="hover:text-accent-600"
            >
              {decodeHtmlEntities(article.title)}
            </a>
          </h1>

          {showHero && (
            <figure className="mb-6 border-y border-edge-subtle py-3">
              <img src={article.imageUrl!} alt="" className="block w-full h-auto" loading="lazy" />
            </figure>
          )}

          {hasContent ? (
            <div
              ref={articleContentRef}
              className="article-content"
              dangerouslySetInnerHTML={{ __html: content }}
            />
          ) : (
            <div ref={articleContentRef}>
              {article.excerpt && (
                <p className="article-content">{article.excerpt}</p>
              )}
              <p className="kicker border-t border-edge-subtle pt-3 mt-4">
                Full text was cleared from the archive.{' '}
                <a
                  href={article.url}
                  onClick={(e) => { e.preventDefault(); openExternalLink(article.url); }}
                  className="text-accent-600 hover:underline underline-offset-2"
                >
                  Read at the source →
                </a>
              </p>
            </div>
          )}

          {highlightPopover && (
            <div
              className="fixed z-30 -translate-x-1/2 -translate-y-full -mt-2 bg-ink text-canvas"
              style={{ left: highlightPopover.x, top: highlightPopover.y }}
              onMouseDown={(e) => e.preventDefault()}
            >
              <button
                onClick={shareWithQuote}
                className="flex items-center gap-1.5 px-3 py-1.5 kicker text-canvas hover:bg-ink-secondary"
              >
                <Share2 className="w-3 h-3" />
                Share with quote
              </button>
            </div>
          )}

          {/* Discussion */}
          <section className="mt-12 border-t border-edge pt-6">
            <div className="kicker text-ink mb-4">
              Discussion · {discussion.length}
            </div>

            <form onSubmit={handleSubmitComment} className="mb-6">
              <div className="relative">
                <textarea
                  ref={commentTextareaRef}
                  value={commentText}
                  onChange={(e) => {
                    setCommentText(e.target.value);
                    setCommentCursorPosition(e.target.selectionStart);
                  }}
                  onSelect={(e) => setCommentCursorPosition(e.currentTarget.selectionStart)}
                  onKeyUp={(e) => setCommentCursorPosition(e.currentTarget.selectionStart)}
                  placeholder="Add a comment. @username to mention someone."
                  rows={3}
                  className="field resize-none"
                />
                <MentionAutocomplete
                  text={commentText}
                  cursorPosition={commentCursorPosition}
                  users={users}
                  onSelectMention={handleSelectCommentMention}
                  textareaRef={commentTextareaRef}
                />
              </div>
              <div className="flex justify-end mt-2">
                <button
                  type="submit"
                  disabled={!commentText.trim() || submittingComment}
                  className="btn btn-ink"
                >
                  {submittingComment ? 'Posting…' : 'Post comment'}
                </button>
              </div>
            </form>

            <div>
              {discussion.map((item) => (
                <div key={`${item.type}-${item.id}`} className="flex gap-3 py-4 border-t border-edge-subtle">
                  <div className="flex-shrink-0">
                    {item.user.avatar ? (
                      <img
                        src={item.user.avatar}
                        alt={item.user.username}
                        className="w-8 h-8 rounded-full object-cover avatar-duotone"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-ink text-canvas flex items-center justify-center font-mono text-xs">
                        {item.user.username[0].toUpperCase()}
                      </div>
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <div className="kicker">
                        <button
                          onClick={() => navigate(`/user/${item.user.id}`)}
                          className="text-ink hover:text-accent-600"
                        >
                          {item.user.displayName || item.user.username}
                        </button>
                        {item.type === 'share' && <> · shared{item.isPublic ? ' publicly' : ''}</>}
                        {' · '}{formatAge(item.createdAt)}
                      </div>

                      {item.type === 'comment' && item.user.id === user.id && (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleEditComment(item.id, item.content)}
                            className="p-1 text-ink-faint hover:text-ink"
                            title="Edit comment"
                          >
                            <Edit3 className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => setShowDeleteConfirm(item.id)}
                            className="p-1 text-ink-faint hover:text-signal"
                            title="Delete comment"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}
                    </div>

                    {item.type === 'comment' && editingCommentId === item.id ? (
                      <div className="space-y-2">
                        <textarea
                          value={editingCommentText}
                          onChange={(e) => setEditingCommentText(e.target.value)}
                          className="field resize-none"
                          rows={3}
                          maxLength={5000}
                        />
                        <div className="flex justify-end gap-2">
                          <button onClick={handleCancelEdit} className="btn btn-quiet">
                            Cancel
                          </button>
                          <button
                            onClick={() => handleSaveEdit(item.id)}
                            disabled={!editingCommentText.trim()}
                            className="btn btn-ink"
                          >
                            Save
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <p className="text-ink-secondary whitespace-pre-wrap text-[15px] leading-relaxed">
                          {renderMentionsAsLinks(item.content, handleMentionClick)}
                        </p>
                        {item.type === 'comment' && item.links && (
                          <CommentLinkEmbed links={JSON.parse(item.links)} />
                        )}
                      </>
                    )}
                  </div>
                </div>
              ))}

              {discussion.length === 0 && (
                <p className="text-sm text-ink-muted py-2">No comments yet.</p>
              )}
            </div>
          </section>
        </div>
      </article>

      {/* Prev / next */}
      <div className="border-t border-edge flex items-stretch">
        <button
          onClick={() => previousArticle && navigate(`/article/${previousArticle.id}`)}
          disabled={!hasPrevious}
          className="tool h-12 pl-4 lg:pl-12"
        >
          <ChevronLeft className="w-3.5 h-3.5" /> Previous
        </button>
        {position && <span className="tool h-12 text-ink-muted">{position}</span>}
        <button
          onClick={() => nextArticle && navigate(`/article/${nextArticle.id}`)}
          disabled={!hasNext}
          className="tool h-12"
        >
          Next <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Delete comment */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-canvas border border-edge w-full max-w-md mx-4">
            <div className="px-5 h-12 flex items-center border-b border-edge">
              <span className="kicker text-ink">Delete comment</span>
            </div>
            <p className="px-5 py-4 text-ink-secondary">
              This removes the comment for everyone. It can't be undone.
            </p>
            <div className="flex justify-end gap-2 px-5 pb-5">
              <button onClick={() => setShowDeleteConfirm(null)} className="btn btn-quiet">
                Cancel
              </button>
              <button onClick={() => handleDeleteComment(showDeleteConfirm)} className="btn btn-danger">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Share */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-canvas border border-edge w-full max-w-md mx-4">
            <div className="px-5 h-12 flex items-center border-b border-edge">
              <span className="kicker text-ink">Share with friends</span>
            </div>

            <form onSubmit={handleShare} className="px-5 py-4 space-y-4">
              <div>
                <label htmlFor="shareNote" className="label">
                  Note · optional
                </label>
                <div className="relative">
                  <textarea
                    ref={shareTextareaRef}
                    id="shareNote"
                    value={shareNote}
                    onChange={(e) => {
                      setShareNote(e.target.value);
                      setShareCursorPosition(e.target.selectionStart);
                    }}
                    onSelect={(e) => setShareCursorPosition(e.currentTarget.selectionStart)}
                    onKeyUp={(e) => setShareCursorPosition(e.currentTarget.selectionStart)}
                    placeholder="Why this one? @username to mention someone."
                    rows={3}
                    className="field resize-none"
                    maxLength={1000}
                    autoFocus
                  />
                  <MentionAutocomplete
                    text={shareNote}
                    cursorPosition={shareCursorPosition}
                    users={users}
                    onSelectMention={handleSelectMention}
                    textareaRef={shareTextareaRef}
                  />
                </div>
                <div className="stamp mt-1">{shareNote.length} / 1000</div>
              </div>

              <label htmlFor="isPublic" className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  id="isPublic"
                  checked={isPublicShare}
                  onChange={(e) => setIsPublicShare(e.target.checked)}
                  className="sr-only"
                />
                <span className={`switch ${isPublicShare ? 'is-on' : ''}`} aria-hidden />
                <span className="text-sm text-ink-secondary">
                  Also publish to the instance's public feed
                </span>
              </label>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowShareModal(false);
                    setShareNote('');
                    setIsPublicShare(user?.defaultPublicShares ?? false);
                  }}
                  className="btn btn-quiet"
                >
                  Cancel
                </button>
                <button type="submit" disabled={submittingShare} className="btn btn-accent">
                  <Share2 className="w-3.5 h-3.5" />
                  {submittingShare ? 'Sharing…' : 'Share'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
